/**
 * NotificationsContext — mounts useRealtimeNotifications ONCE per layout.
 *
 * Problem solved: GlobalAlerts and HeaderSection both called useRealtimeNotifications(),
 * creating two separate useChannel subscriptions to NotificationsChannel. On every
 * WS push, handleNotificationMessage fired twice → new notifications appeared doubled
 * in the React Query cache.
 *
 * Fix: mount the hook once here, share state via context.
 */
import { createContext, useContext, useMemo } from 'react';

import { type Notification, useRealtimeNotifications } from '@/domains/notifications/api';

type RealtimeValue = ReturnType<typeof useRealtimeNotifications>;

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  systemAlert: RealtimeValue['systemAlert'];
  /** Unread mandatory (compliance/security) notifications that require acknowledgment */
  pendingMandatory: Notification[];
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissSystemAlert: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const value = useRealtimeNotifications();

  const pendingMandatory = useMemo(
    () => value.notifications.filter(n => n.mandatory && !n.read),
    [value.notifications]
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications: value.notifications,
        unreadCount: value.unreadCount,
        systemAlert: value.systemAlert,
        pendingMandatory,
        markAsRead: value.markAsRead,
        markAllAsRead: value.markAllAsRead,
        dismissSystemAlert: value.dismissSystemAlert,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used inside NotificationsProvider');
  return ctx;
}
